import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { storeToRefs } from 'pinia'
import { addBlockList, setTableData, setTableElement } from '@/views/editor/composable/useBlockCreate'
import { stripFunctionCloseButton } from '@/composables/common.ts'
import { deletePrefix, addPrefix } from '@/views/editor/composable/useNoteUtils'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable'
import { calcFunction } from '@/views/editor/composable/table/useFunction.ts'
import { nextTick } from 'vue'
import { newBlockEvent } from '@/views/editor/composable/useBlockEvent'
import { useChart } from '@/composables/chart/useChart'

// ==================== Types ====================

interface Change {
	type: 'block-modify' | 'block-create' | 'block-delete' | 'cell-modify'
	blockUid?: string
	cellUid?: string
	blockType?: string
	beforeContent?: any
	afterContent?: any
	beforeType?: string
	afterType?: string
	beforeDataType?: any
	afterDataType?: any
	sortOrder?: number
	prevBlockUid?: string
}

interface DeltaEntry {
	changes: Change[]
	beforeBlockOrder: string[]
	afterBlockOrder: string[]
	selection: any
	beforeSelection: any
	timestamp: number
	metadata: any
}

// ==================== Module-level baseline state ====================

let _lastPushedBlocks: any[] = []
let _undoRedoResetTimer: ReturnType<typeof setTimeout> | null = null

function cleanImageInContent(content: any): any {
	if (typeof content !== 'string') return content
	return content.replace(/<img([^>]+)src="data:image[^"]*"([^>]*)>/g, '<img$1src="[IMAGE_PLACEHOLDER]"$2>')
}

// ==================== Exported baseline update functions ====================
// 소켓으로 원격 변경이 수신되면 베이스라인을 갱신하여,
// 다음 push() 시 원격 변경이 로컬 델타에 포함되지 않도록 함

export function updateBaselineForRemoteModify(blockUid: string, content: any, blockType?: string) {
	const block = _lastPushedBlocks.find((b: any) => b.blockUid === blockUid)
	if (block) {
		block.content = typeof content === 'string' ? cleanImageInContent(content) : JSON.parse(JSON.stringify(content))
		if (blockType) block.type = blockType
	}
}

export function updateBaselineForRemoteCreate(blockUid: string, content: any, blockType: string, afterBlockUid?: string) {
	if (_lastPushedBlocks.find((b: any) => b.blockUid === blockUid)) return
	const newBlock = {
		blockUid,
		content: typeof content === 'string' ? cleanImageInContent(content) : JSON.parse(JSON.stringify(content)),
		type: blockType,
		sortOrder: _lastPushedBlocks.length,
	}
	if (afterBlockUid) {
		const idx = _lastPushedBlocks.findIndex((b: any) => b.blockUid === afterBlockUid)
		_lastPushedBlocks.splice(idx >= 0 ? idx + 1 : _lastPushedBlocks.length, 0, newBlock)
	} else {
		_lastPushedBlocks.push(newBlock)
	}
	_lastPushedBlocks.forEach((b: any, i: number) => (b.sortOrder = i))
}

export function updateBaselineForRemoteDelete(blockUid: string) {
	_lastPushedBlocks = _lastPushedBlocks.filter((b: any) => b.blockUid !== blockUid)
	_lastPushedBlocks.forEach((b: any, i: number) => (b.sortOrder = i))
}

export function updateBaselineForRemoteCellUpdate(blockUid: string, cellUid: string, content: string, dataType?: any) {
	const block = _lastPushedBlocks.find((b: any) => b.blockUid === blockUid)
	if (block?.type === 'defaultTable' && block.content?.cell) {
		for (const row of block.content.cell) {
			for (const cell of row) {
				if ((cell.cellUid || cell.cellUID) === cellUid) {
					cell.content = content
					if (dataType) cell.dataType = JSON.parse(JSON.stringify(dataType))
					return
				}
			}
		}
	}
}

export function updateBaselineForRemoteBlockMove(blockUid: string, prevBlockUid: string) {
	const blockIdx = _lastPushedBlocks.findIndex((b: any) => b.blockUid === blockUid)
	if (blockIdx < 0) return
	const [block] = _lastPushedBlocks.splice(blockIdx, 1)
	const prevIdx = _lastPushedBlocks.findIndex((b: any) => b.blockUid === prevBlockUid)
	_lastPushedBlocks.splice(prevIdx >= 0 ? prevIdx + 1 : _lastPushedBlocks.length, 0, block)
	_lastPushedBlocks.forEach((b: any, i: number) => (b.sortOrder = i))
}

export function updateBaselineBlockId(tempId: string, realId: string) {
	const block = _lastPushedBlocks.find((b: any) => b.blockUid === tempId)
	if (block) block.blockUid = realId
}

export function updateBaselineForRemoteTableBlock(blockUid: string, content: any) {
	const block = _lastPushedBlocks.find((b: any) => b.blockUid === blockUid)
	if (block) {
		block.content = JSON.parse(JSON.stringify(content))
		block.type = 'defaultTable'
	}
}

// ==================== UseHistory Class ====================

export class UseHistory {
	private MAX_STACK_SIZE = 50

	constructor() {}

	// ==================== DOM → Block 캡처 (기존 유지) ====================

	private getBlocksForHistory(): any[] {
		const { chartData } = storeToRefs(useEditorStore())
		const blockItems = document.querySelectorAll('.editor--block')
		const blocks: any = []

		blockItems.forEach((item: any, index: number) => {
			const data: any = {
				blockUid: deletePrefix(item.id),
				sortOrder: index,
				type: item.dataset.type,
			}

			if (item.dataset.type === 'defaultTable') {
				try {
					data.content = setTableData(item.querySelector('table'), true)
				} catch (e) {
					data.content = ''
				}
			} else if (item.dataset.type === 'chart') {
				chartData.value.forEach((chart: any) => {
					if (chart.blockUid === deletePrefix(item.id)) {
						data.content = chart.chartDataList
					}
				})
			} else if (item.dataset.type === 'file') {
				const blockFileList: any = [...item.querySelectorAll('.file--content__list')]
				const blockUuid: any = []
				blockFileList.forEach((fileItem: any) => {
					blockUuid.push({
						originName: fileItem.dataset.originName,
						uuidFileName: fileItem.dataset.file,
					})
				})
				data.content = blockUuid
			} else {
				data.content = item.innerHTML
			}

			blocks.push(data)
		})

		return blocks
	}

	private cleanImageTags(content: string): string {
		if (typeof content !== 'string') return content
		return content.replace(/<img([^>]+)src="data:image[^"]*"([^>]*)>/g, '<img$1src="[IMAGE_PLACEHOLDER]"$2>')
	}

	private removeImageDataForHistory(blocks: any[]): any[] {
		return blocks.map(block => {
			const cleanBlock = { ...block }

			if (block.type === 'image' && block.content && typeof block.content === 'string') {
				cleanBlock.content = block.content.replace(/<img[^>]+src="data:image[^"]*"[^>]*>/g, '<img src="[IMAGE_PLACEHOLDER]">')
			}

			if (block.type === 'text' && block.content && typeof block.content === 'string' && block.content.includes('<img')) {
				cleanBlock.content = this.cleanImageTags(block.content)
			}

			if (block.type === 'defaultTable' && block.content && typeof block.content === 'string') {
				cleanBlock.content = this.cleanImageTags(block.content)
			}

			return cleanBlock
		})
	}

	// ==================== 커서 복원 헬퍼 (기존 유지) ====================

	private async restoreCursorWithFallback(blockEl: HTMLElement, cellTarget: HTMLTableCellElement | null, selectionState: any): Promise<void> {
		if (cellTarget) {
			const cellDiv = cellTarget.querySelector('div[contenteditable="true"]') as HTMLElement | null
			if (cellDiv) {
				cellDiv.focus()
				await nextTick()
				try {
					new UseSelection().isCursorCheckPoint()
				} catch (e) {
					const sel = window.getSelection()
					const range = document.createRange()
					range.setStart(cellDiv, 0)
					range.collapse(true)
					sel?.removeAllRanges()
					sel?.addRange(range)
				}
			}
		} else {
			let targetEl: HTMLElement = blockEl
			let contentEditableEl: HTMLElement | null = blockEl.querySelector('[contenteditable="true"]') as HTMLElement
			if (contentEditableEl) {
				targetEl = contentEditableEl
			}

			let focusableEl: HTMLElement | null = contentEditableEl
			if (!focusableEl) {
				if (blockEl.getAttribute('contenteditable') === 'true') {
					focusableEl = blockEl
				} else {
					focusableEl = blockEl.closest('[contenteditable="true"]') as HTMLElement
				}
			}
			if (!focusableEl) return

			const targetStartOffset = selectionState?.startAbs ?? null
			const targetEndOffset = selectionState?.endAbs ?? null

			const setCursor = () => {
				if (document.activeElement !== focusableEl) {
					focusableEl!.focus({ preventScroll: true })
				}

				const sel = window.getSelection()
				const range = document.createRange()

				const findTextNodePosition = (offset: number | null): { node: Text | null; offset: number } => {
					const walker = document.createTreeWalker(targetEl, NodeFilter.SHOW_TEXT, null)
					let currentOffset = 0
					let targetNode: Text | null = null
					let nodeOffset = 0
					let lastTextNode: Text | null = null
					let node: Text | null

					while ((node = walker.nextNode() as Text | null)) {
						lastTextNode = node
						const nodeLength = node.textContent?.length || 0

						if (offset !== null && currentOffset + nodeLength >= offset) {
							targetNode = node
							nodeOffset = offset - currentOffset
							break
						}
						currentOffset += nodeLength
					}

					if (offset === null) {
						if (lastTextNode) {
							return { node: lastTextNode, offset: lastTextNode.textContent?.length || 0 }
						}
						return { node: null, offset: 0 }
					} else if (targetNode) {
						const maxOffset = targetNode.textContent?.length || 0
						return { node: targetNode, offset: Math.min(nodeOffset, maxOffset) }
					} else if (lastTextNode) {
						return { node: lastTextNode, offset: lastTextNode.textContent?.length || 0 }
					}
					return { node: null, offset: 0 }
				}

				const startPos = findTextNodePosition(targetStartOffset)
				const endPos = findTextNodePosition(targetEndOffset)

				if (targetStartOffset === null && targetEndOffset === null) {
					if (startPos.node) {
						range.setStart(startPos.node, startPos.offset)
					} else {
						range.selectNodeContents(targetEl)
						range.collapse(false)
					}
				} else if (startPos.node) {
					range.setStart(startPos.node, startPos.offset)
					if (targetEndOffset !== null && targetEndOffset !== targetStartOffset && endPos.node) {
						range.setEnd(endPos.node, endPos.offset)
					} else {
						range.collapse(true)
					}
				} else if (!startPos.node) {
					range.setStart(targetEl, 0)
					range.collapse(true)
				}

				sel?.removeAllRanges()
				sel?.addRange(range)
			}

			setCursor()
		}
	}

	private addActiveClassToBlock(blockEl: HTMLElement): void {
		if (blockEl && blockEl.isConnected) {
			document.querySelectorAll('.editor--block.active').forEach(el => {
				if (el !== blockEl) {
					el.classList.remove('active')
				}
			})

			if (blockEl.dataset.type !== 'defaultTable') {
				let attempts = 0
				const intervalId = setInterval(() => {
					attempts++
					if (blockEl && blockEl.isConnected) {
						if (!blockEl.classList.contains('active')) {
							blockEl.classList.add('active')
						} else {
							clearInterval(intervalId)
						}
					}
					if (attempts >= 10) {
						clearInterval(intervalId)
					}
				}, 50)
			}
		}
	}

	// ==================== 델타 계산 ====================

	private computeDelta(prevBlocks: any[], currBlocks: any[]): Change[] {
		const changes: Change[] = []
		const prevMap = new Map<string, any>(prevBlocks.map(b => [b.blockUid, b]))
		const currMap = new Map<string, any>(currBlocks.map(b => [b.blockUid, b]))

		// 수정된 블럭
		for (const [uid, currBlock] of currMap) {
			const prevBlock = prevMap.get(uid)
			if (!prevBlock) continue

			if (currBlock.type === 'defaultTable' && prevBlock.type === 'defaultTable') {
				const cellChanges = this.computeTableCellDelta(uid, prevBlock.content, currBlock.content)
				changes.push(...cellChanges)
			} else {
				const prevContent = JSON.stringify(prevBlock.content)
				const currContent = JSON.stringify(currBlock.content)
				if (prevContent !== currContent || prevBlock.type !== currBlock.type) {
					changes.push({
						type: 'block-modify',
						blockUid: uid,
						blockType: currBlock.type,
						beforeContent: prevBlock.content,
						afterContent: currBlock.content,
						beforeType: prevBlock.type,
						afterType: currBlock.type,
					})
				}
			}
		}

		// 새로 생성된 블럭
		const currArray = [...currMap.entries()]
		for (let i = 0; i < currArray.length; i++) {
			const [uid, currBlock] = currArray[i]
			if (!prevMap.has(uid)) {
				changes.push({
					type: 'block-create',
					blockUid: uid,
					blockType: currBlock.type,
					afterContent: currBlock.content,
					sortOrder: i,
					prevBlockUid: i > 0 ? currArray[i - 1][0] : undefined,
				})
			}
		}

		// 삭제된 블럭
		const prevArray = [...prevMap.entries()]
		for (let i = 0; i < prevArray.length; i++) {
			const [uid, prevBlock] = prevArray[i]
			if (!currMap.has(uid)) {
				changes.push({
					type: 'block-delete',
					blockUid: uid,
					blockType: prevBlock.type,
					beforeContent: prevBlock.content,
					beforeType: prevBlock.type,
					sortOrder: i,
					prevBlockUid: i > 0 ? prevArray[i - 1][0] : undefined,
				})
			}
		}

		return changes
	}

	private computeTableCellDelta(blockUid: string, prevContent: any, currContent: any): Change[] {
		if (!prevContent?.cell || !currContent?.cell) {
			if (JSON.stringify(prevContent) !== JSON.stringify(currContent)) {
				return [
					{
						type: 'block-modify',
						blockUid,
						blockType: 'defaultTable',
						beforeContent: prevContent,
						afterContent: currContent,
					},
				]
			}
			return []
		}

		const prevCellMap = new Map<string, any>()
		const currCellMap = new Map<string, any>()

		for (const row of prevContent.cell) {
			for (const cell of row) {
				const uid = cell.cellUid || cell.cellUID
				if (uid) prevCellMap.set(uid, cell)
			}
		}
		for (const row of currContent.cell) {
			for (const cell of row) {
				const uid = cell.cellUid || cell.cellUID
				if (uid) currCellMap.set(uid, cell)
			}
		}

		// 셀 구조가 변경된 경우 (셀 추가/삭제/병합) → 블럭 레벨 변경으로 처리
		const prevUids = [...prevCellMap.keys()].sort()
		const currUids = [...currCellMap.keys()].sort()
		const structureChanged = JSON.stringify(prevUids) !== JSON.stringify(currUids)

		// allSize 변경 (행/열 크기 조정) → 블럭 레벨 변경으로 처리
		const sizeChanged = JSON.stringify(prevContent.allSize) !== JSON.stringify(currContent.allSize)

		if (structureChanged || sizeChanged) {
			return [
				{
					type: 'block-modify',
					blockUid,
					blockType: 'defaultTable',
					beforeContent: prevContent,
					afterContent: currContent,
				},
			]
		}

		// 동일한 셀 구조 → 셀 레벨 비교
		const changes: Change[] = []
		for (const [uid, currCell] of currCellMap) {
			const prevCell = prevCellMap.get(uid)
			if (!prevCell) continue

			const contentChanged = prevCell.content !== currCell.content
			const dataTypeChanged = JSON.stringify(prevCell.dataType) !== JSON.stringify(currCell.dataType)

			if (contentChanged || dataTypeChanged) {
				changes.push({
					type: 'cell-modify',
					blockUid,
					cellUid: uid,
					beforeContent: prevCell.content,
					afterContent: currCell.content,
					beforeDataType: prevCell.dataType,
					afterDataType: currCell.dataType,
				})
			}
		}

		return changes
	}

	// ==================== push ====================

	public async push(skipSetSelection = false, metadata: any = {}) {
		const { undoStack, redoStack, changeRange, isPasting, isTemplateAdding, isUndoRedoInProgress } = storeToRefs(useEditorStore())

		if (isUndoRedoInProgress.value) return
		if (isPasting.value) return
		if (isTemplateAdding.value) return

		const blocks = this.getBlocksForHistory()
		const optimizedBlocks = this.removeImageDataForHistory(blocks)

		// 첫 push(reset 직후) → 베이스라인만 설정
		if (_lastPushedBlocks.length === 0) {
			_lastPushedBlocks = JSON.parse(JSON.stringify(optimizedBlocks))

			if (!skipSetSelection) {
				new UseSelection().setChangeSelection()
				await nextTick()
			}
			return
		}

		// 셀렉션 캡처
		if (!skipSetSelection) {
			new UseSelection().setChangeSelection()
			await nextTick()
		}
		const currentSelection = JSON.parse(JSON.stringify(changeRange.value))

		// 델타 계산
		const changes = this.computeDelta(_lastPushedBlocks, optimizedBlocks)

		// 블럭 순서 비교
		const beforeOrder = _lastPushedBlocks.map((b: any) => b.blockUid)
		const afterOrder = optimizedBlocks.map((b: any) => b.blockUid)

		// 변경 없으면 스킵
		if (changes.length === 0 && JSON.stringify(beforeOrder) === JSON.stringify(afterOrder)) {
			return
		}

		// 이전 셀렉션 (undo 시 복원할 커서 위치)
		const prevEntry = undoStack.value[undoStack.value.length - 1] as DeltaEntry | undefined
		const beforeSelection = prevEntry?.selection || null

		const entry: DeltaEntry = {
			changes,
			beforeBlockOrder: beforeOrder,
			afterBlockOrder: afterOrder,
			selection: currentSelection,
			beforeSelection,
			timestamp: Date.now(),
			metadata: metadata,
		}

		undoStack.value.push(entry)

		if (undoStack.value.length > this.MAX_STACK_SIZE) {
			undoStack.value.shift()
		}

		// 베이스라인 갱신
		_lastPushedBlocks = JSON.parse(JSON.stringify(optimizedBlocks))

		// redoStack 초기화 (undo/redo 진행 중이 아닌 새 액션인 경우)
		if (!isUndoRedoInProgress.value) {
			redoStack.value = []
		}
	}

	// ==================== reset ====================

	public reset() {
		const { undoStack, redoStack } = storeToRefs(useEditorStore())
		undoStack.value = []
		redoStack.value = []
		_lastPushedBlocks = []
	}

	public redoReset() {
		const { redoStack } = storeToRefs(useEditorStore())
		redoStack.value = []
	}

	// ==================== undo ====================

	public async undo(): Promise<boolean> {
		const { undoStack, redoStack, changeRange, selector, isUndoRedoInProgress, lastUndoRedoTime } = storeToRefs(useEditorStore())

		if (undoStack.value.length === 0) return false

		if (_undoRedoResetTimer) {
			clearTimeout(_undoRedoResetTimer)
			_undoRedoResetTimer = null
		}

		isUndoRedoInProgress.value = true
		lastUndoRedoTime.value = Date.now()

		try {
			try {
				const { getGlobalTypingHistoryManager } = await import('@/views/editor/composable/table/useTable.ts')
				const typingManager = getGlobalTypingHistoryManager()
				if (typingManager) typingManager.cleanup()
				const { getEditorTypingHistoryManager } = await import('@/views/editor/composable/useEditorInput.ts')
				const editorTypingManager = getEditorTypingHistoryManager()
				if (editorTypingManager) editorTypingManager.cleanup()
			} catch (e) {}

			const entry = undoStack.value.pop() as DeltaEntry
			if (!entry) {
				isUndoRedoInProgress.value = false
				return false
			}

			redoStack.value.push(entry)

			await this.applyDelta(entry, true)

			const createdFirstBlock = this.ensureFirstBlockExists()

			await this.postApplyProcessing(entry)

			_lastPushedBlocks = JSON.parse(JSON.stringify(this.removeImageDataForHistory(this.getBlocksForHistory())))

			const { noteType, noteUid } = storeToRefs(useEditorStore())
			const { socket } = storeToRefs(useCommonStore())
			if (noteType.value === 'Project' && socket.value && noteUid.value) {
				await this.syncDeltaSocket(entry, true, noteUid.value, socket.value)
				if (createdFirstBlock) {
					socket.value.modifyNoteBlock(noteUid.value, createdFirstBlock.blockUid, createdFirstBlock.content, 'text')
				}
			}

			await nextTick()
			await new Promise(requestAnimationFrame)
			await new Promise(requestAnimationFrame)

			if (entry.metadata?.isDragMove) {
				document.querySelectorAll('.editor--block.active').forEach(el => el.classList.remove('active'))
				const activeEl = document.activeElement as HTMLElement
				if (activeEl && activeEl !== document.body && typeof activeEl.blur === 'function') {
					activeEl.blur()
				}
				window.getSelection()?.removeAllRanges()
			} else {
				await this.restoreCursorAfterDelta(entry, true)
			}

			return true
		} catch (e) {
			console.error('[UseHistory.undo] error:', e)
			return false
		} finally {
			if (_undoRedoResetTimer) {
				clearTimeout(_undoRedoResetTimer)
			}
			_undoRedoResetTimer = setTimeout(() => {
				_undoRedoResetTimer = null
				useDeleteTable().functionList(true)
				const { isFunctionActive, functionTarget } = storeToRefs(useEditorStore())
				isFunctionActive.value = false
				functionTarget.value = null
				isUndoRedoInProgress.value = false
			}, 100)
		}
	}

	// ==================== redo ====================

	public async redo(): Promise<boolean> {
		const { undoStack, redoStack, changeRange, selector, isUndoRedoInProgress, lastUndoRedoTime } = storeToRefs(useEditorStore())

		if (redoStack.value.length === 0) return false

		if (_undoRedoResetTimer) {
			clearTimeout(_undoRedoResetTimer)
			_undoRedoResetTimer = null
		}

		isUndoRedoInProgress.value = true
		lastUndoRedoTime.value = Date.now()

		try {
			try {
				const { getGlobalTypingHistoryManager } = await import('@/views/editor/composable/table/useTable.ts')
				const typingManager = getGlobalTypingHistoryManager()
				if (typingManager) typingManager.cleanup()
				const { getEditorTypingHistoryManager } = await import('@/views/editor/composable/useEditorInput.ts')
				const editorTypingManager = getEditorTypingHistoryManager()
				if (editorTypingManager) editorTypingManager.cleanup()
			} catch (e) {}

			const entry = redoStack.value.pop() as DeltaEntry
			if (!entry) {
				isUndoRedoInProgress.value = false
				return false
			}

			undoStack.value.push(entry)

			await this.applyDelta(entry, false)

			await this.postApplyProcessing(entry)

			_lastPushedBlocks = JSON.parse(JSON.stringify(this.removeImageDataForHistory(this.getBlocksForHistory())))

			const { noteType, noteUid } = storeToRefs(useEditorStore())
			const { socket } = storeToRefs(useCommonStore())
			if (noteType.value === 'Project' && socket.value && noteUid.value) {
				await this.syncDeltaSocket(entry, false, noteUid.value, socket.value)
			}

			await nextTick()
			await new Promise(requestAnimationFrame)
			await new Promise(requestAnimationFrame)

			await this.restoreCursorAfterDelta(entry, false)

			return true
		} catch (e) {
			console.error('[UseHistory.redo] error:', e)
			return false
		} finally {
			if (_undoRedoResetTimer) {
				clearTimeout(_undoRedoResetTimer)
			}
			_undoRedoResetTimer = setTimeout(() => {
				_undoRedoResetTimer = null
				useDeleteTable().functionList(true)
				const { isFunctionActive, functionTarget } = storeToRefs(useEditorStore())
				isFunctionActive.value = false
				functionTarget.value = null
				isUndoRedoInProgress.value = false
			}, 100)
		}
	}

	// ==================== 델타를 DOM에 적용 ====================

	private async applyDelta(entry: DeltaEntry, isUndo: boolean) {
		const activeEl = document.activeElement as HTMLElement
		if (activeEl && activeEl !== document.body && typeof activeEl.blur === 'function') {
			activeEl.blur()
		}

		const changes = isUndo ? [...entry.changes].reverse() : entry.changes

		for (const change of changes) {
			switch (change.type) {
				case 'block-modify':
					this.applyBlockModifyToDOM(change, isUndo)
					break
				case 'block-create':
					if (isUndo) {
						this.applyBlockRemoveFromDOM(change)
					} else {
						this.applyBlockCreateToDOM(change)
					}
					break
				case 'block-delete':
					if (isUndo) {
						this.applyBlockCreateToDOM(change)
					} else {
						this.applyBlockRemoveFromDOM(change)
					}
					break
				case 'cell-modify':
					this.applyCellModifyToDOM(change, isUndo)
					break
			}
		}

		// 블럭 순서 복원 (순서가 실제로 변경된 경우에만)
		if (JSON.stringify(entry.beforeBlockOrder) !== JSON.stringify(entry.afterBlockOrder)) {
			const targetOrder = isUndo ? entry.beforeBlockOrder : entry.afterBlockOrder
			this.reorderBlocks(targetOrder)
		}

		await nextTick()
	}

	private applyBlockModifyToDOM(change: Change, isUndo: boolean) {
		const content = isUndo ? change.beforeContent : change.afterContent
		const blockType = isUndo ? change.beforeType || change.blockType : change.afterType || change.blockType
		const blockId = addPrefix(change.blockUid!)
		const blockEl = document.getElementById(blockId)
		if (!blockEl) return

		// 타입 변경 처리
		if (change.beforeType && change.afterType && change.beforeType !== change.afterType) {
			const targetType = isUndo ? change.beforeType : change.afterType
			blockEl.dataset.type = targetType
			blockEl.setAttribute('contenteditable', ['defaultTable', 'chart', 'image', 'file'].includes(targetType!) ? 'false' : 'true')
		}

		if (blockType === 'defaultTable') {
			this.applyTableBlockToDOM(blockEl, content, change.blockUid!)
		} else if (blockType === 'chart') {
			this.applyChartBlockToDOM(blockEl, content, change.blockUid!)
		} else {
			blockEl.innerHTML = typeof content === 'string' ? content : JSON.stringify(content)
		}
	}

	private applyTableBlockToDOM(blockEl: HTMLElement, content: any, blockUid: string) {
		if (!content || !content.cell) {
			blockEl.innerHTML = ''
			return
		}

		const container = blockEl.closest('.editor--block__container')
		if (!container) return

		const nextSibling = container.nextElementSibling
		const parent = container.parentElement
		container.remove()

		const newContainer = document.createElement('div')
		newContainer.classList.add('editor--block__container')
		newContainer.contentEditable = 'false'

		const newBlock = document.createElement('div')
		newBlock.id = addPrefix(blockUid)
		newBlock.classList.add('editor--block')
		newBlock.setAttribute('data-type', 'defaultTable')
		newBlock.setAttribute('contenteditable', 'false')
		newBlock.setAttribute('tabIndex', '1')

		const tableHTML = setTableElement({
			content: {
				tableUid: content.tableUid,
				rows: content.cell || [],
				allSize: content.allSize || { width: [], height: [] },
			},
		})
		newBlock.innerHTML = tableHTML

		newContainer.appendChild(newBlock)

		if (nextSibling) {
			parent?.insertBefore(newContainer, nextSibling)
		} else {
			parent?.appendChild(newContainer)
		}
	}

	private applyChartBlockToDOM(blockEl: HTMLElement, content: any, blockUid: string) {
		const { chartDataList } = storeToRefs(useEditorStore())
		try {
			const chartData = typeof content === 'string' ? JSON.parse(content) : content
			const existingIndex = chartDataList.value.findIndex((c: any) => c.blockUid === blockUid)

			if (existingIndex >= 0) {
				chartDataList.value[existingIndex] = { blockUid, chartDataList: chartData }
			} else {
				chartDataList.value.push({ blockUid, chartDataList: chartData })
			}

			chartDataList.value.chartTargetUid = blockUid
			useChart().init()
		} catch (e) {
			console.error('[applyChartBlockToDOM] 차트 렌더링 실패:', e)
		}
	}

	private applyBlockCreateToDOM(change: Change) {
		const editor = document.getElementById('editor')
		if (!editor) return

		const blockUid = change.blockUid!
		const blockType = change.type === 'block-create' ? change.blockType! : change.beforeType || change.blockType!
		const content = change.type === 'block-create' ? change.afterContent : change.beforeContent

		if (document.getElementById(addPrefix(blockUid))) return

		const newContainer = document.createElement('div')
		newContainer.classList.add('editor--block__container')
		newContainer.contentEditable = 'false'

		const newBlock = document.createElement('div')
		newBlock.id = addPrefix(blockUid)
		newBlock.classList.add('editor--block')
		newBlock.setAttribute('data-type', blockType)
		newBlock.setAttribute('contenteditable', ['defaultTable', 'chart', 'image', 'file'].includes(blockType) ? 'false' : 'true')
		newBlock.setAttribute('tabIndex', '1')

		if (blockType === 'defaultTable' && content?.cell) {
			const tableHTML = setTableElement({
				content: {
					tableUid: content.tableUid,
					rows: content.cell || [],
					allSize: content.allSize || { width: [], height: [] },
				},
			})
			newBlock.innerHTML = tableHTML
		} else if (typeof content === 'string') {
			newBlock.innerHTML = content
		} else {
			newBlock.innerHTML = content ? JSON.stringify(content) : ''
		}

		newContainer.appendChild(newBlock)

		// 삽입 위치 결정
		const prevBlockUid = change.prevBlockUid
		if (prevBlockUid) {
			const prevBlockEl = document.getElementById(addPrefix(prevBlockUid))
			if (prevBlockEl) {
				const prevContainer = prevBlockEl.closest('.editor--block__container')
				if (prevContainer?.nextSibling) {
					editor.insertBefore(newContainer, prevContainer.nextSibling)
				} else {
					editor.appendChild(newContainer)
				}
				return
			}
		}

		// sortOrder 기반 삽입
		const sortOrder = change.sortOrder ?? 0
		const allContainers = editor.querySelectorAll('.editor--block__container')
		if (sortOrder < allContainers.length) {
			editor.insertBefore(newContainer, allContainers[sortOrder])
		} else {
			editor.appendChild(newContainer)
		}
	}

	private applyBlockRemoveFromDOM(change: Change) {
		const blockUid = change.type === 'block-create' ? change.blockUid! : change.blockUid!
		const blockEl = document.getElementById(addPrefix(blockUid))
		if (blockEl) {
			const container = blockEl.closest('.editor--block__container')
			if (container) {
				container.remove()
			}
		}
	}

	private applyCellModifyToDOM(change: Change, isUndo: boolean) {
		const content = isUndo ? change.beforeContent : change.afterContent
		const dataType = isUndo ? change.beforeDataType : change.afterDataType
		const cellId = addPrefix(change.cellUid!)
		const cellEl = document.getElementById(cellId) as HTMLTableCellElement

		if (!cellEl) return

		// 기존 dataset 속성 전부 제거 후 대상 dataType 재적용
		Object.keys(cellEl.dataset).forEach(key => {
			delete cellEl.dataset[key]
		})

		if (dataType) {
			Object.entries(dataType).forEach(([key, value]) => {
				if (typeof value === 'string') {
					cellEl.dataset[key] = value
				}
			})
		}

		// function 여부에 따라 div 구조 및 contenteditable 복원
		const isFunction = dataType?.type === 'function'
		const div = cellEl.querySelector('div') as HTMLElement | null
		if (!div) return

		if (isFunction) {
			div.setAttribute('contenteditable', 'false')
			div.innerHTML = `<span>${stripFunctionCloseButton(content ?? '')}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
		} else {
			div.setAttribute('contenteditable', 'true')
			div.innerHTML = content ?? ''
		}
	}

	private ensureFirstBlockExists(): { blockUid: string; content: string } | null {
		const editor = document.getElementById('editor')
		if (!editor) return null

		// display: none으로 숨겨진 블럭 컨테이너를 다시 보이게 복원
		const hiddenContainers = editor.querySelectorAll('.editor--block__container')
		hiddenContainers.forEach((container: Element) => {
			const htmlContainer = container as HTMLElement
			if (htmlContainer.style.display === 'none') {
				htmlContainer.style.display = ''
			}
		})

		const existingBlocks = editor.querySelectorAll('.editor--block')
		if (existingBlocks.length > 0) return null

		const { isWrite } = storeToRefs(useEditorStore())
		const blockUid = `temp-${useCommonStore().uniqueId()}`

		const newContainer = document.createElement('div')
		newContainer.classList.add('editor--block__container')
		newContainer.contentEditable = 'false'

		const newBlock = document.createElement('div')
		newBlock.id = addPrefix(blockUid)
		newBlock.classList.add('editor--block', 'active')
		newBlock.dataset.type = 'text'
		newBlock.setAttribute('contenteditable', 'true')
		newBlock.setAttribute('tabIndex', '1')
		newBlock.innerHTML = '\u200B'

		newContainer.appendChild(newBlock)
		editor.appendChild(newContainer)

		return { blockUid, content: '\u200B' }
	}

	private reorderBlocks(targetOrder: string[]) {
		const editor = document.getElementById('editor')
		if (!editor || targetOrder.length === 0) return

		const currentBlocks = [...editor.querySelectorAll(':scope > .editor--block__container > .editor--block')]
		const currentOrder = currentBlocks.map(b => deletePrefix(b.id))
		if (JSON.stringify(currentOrder) === JSON.stringify(targetOrder)) return

		const lastBuffer = editor.querySelector(':scope > .editor--scroll__buffer.last')

		for (const uid of targetOrder) {
			const blockEl = document.getElementById(addPrefix(uid))
			if (blockEl) {
				const container = blockEl.closest('.editor--block__container')
				if (container && container.parentElement === editor) {
					if (lastBuffer) {
						editor.insertBefore(container, lastBuffer)
					} else {
						editor.appendChild(container)
					}
				}
			}
		}
	}

	// ==================== 후처리 ====================

	private async postApplyProcessing(entry: DeltaEntry) {
		const { blockList } = storeToRefs(useEditorStore())

		const hasTableChanges = entry.changes.some(
			c =>
				(c.type === 'block-modify' && (c.blockType === 'defaultTable' || c.beforeType === 'defaultTable')) ||
				(c.type === 'block-create' && c.blockType === 'defaultTable') ||
				(c.type === 'block-delete' && (c.blockType === 'defaultTable' || c.beforeType === 'defaultTable')),
		)

		const hasCellChanges = entry.changes.some(c => c.type === 'cell-modify')

		if (hasTableChanges) {
			await useCreateTable().event()
			await nextTick()
			const allTables = document.querySelectorAll('.editor--block[data-type="defaultTable"] table')
			allTables.forEach((table: any) => {
				useCreateTable().tableShapes(table)
			})
		}

		if (hasTableChanges || hasCellChanges) {
			calcFunction()
		}

		// blockList 동기화
		blockList.value = JSON.parse(JSON.stringify(this.getBlocksForHistory()))

		await nextTick()
		newBlockEvent()
	}

	// ==================== 소켓 동기화 ====================

	private async syncDeltaSocket(entry: DeltaEntry, isUndo: boolean, noteUid: string, socket: any) {
		if (!socket) return

		const changes = isUndo ? [...entry.changes].reverse() : entry.changes

		for (const change of changes) {
			switch (change.type) {
				case 'block-modify':
					this.syncBlockModifySocket(change, isUndo, noteUid, socket)
					break
				case 'block-create':
					if (isUndo) {
						socket.deleteNoteBlock(noteUid, change.blockUid)
					} else {
						this.syncBlockCreateSocket(change, noteUid, socket)
					}
					break
				case 'block-delete':
					if (isUndo) {
						this.syncBlockRestoreSocket(change, noteUid, socket)
					} else {
						socket.deleteNoteBlock(noteUid, change.blockUid)
					}
					break
				case 'cell-modify':
					this.syncCellModifySocket(change, isUndo, noteUid, socket)
					break
			}
		}
	}

	private syncBlockModifySocket(change: Change, isUndo: boolean, noteUid: string, socket: any) {
		const content = isUndo ? change.beforeContent : change.afterContent
		const blockType = isUndo ? change.beforeType || change.blockType : change.afterType || change.blockType
		const blockUid = deletePrefix(change.blockUid!)

		if (blockType === 'defaultTable' && content?.cell) {
			socket.createTableBlock({
				noteUID: noteUid,
				blockUID: blockUid,
				new: false,
				content: {
					type: 'defaultTable',
					tableUID: content.tableUid || '',
					cell: content.cell || [],
				},
			})
		} else if (blockType === 'chart') {
			const chartContent = typeof content === 'string' ? content : JSON.stringify(content)
			socket.modifyNoteBlock(noteUid, blockUid, chartContent, 'chart')
		} else {
			socket.modifyNoteBlock(noteUid, blockUid, content, blockType || 'text')
		}
	}

	private syncBlockCreateSocket(change: Change, noteUid: string, socket: any) {
		const blockType = change.blockType || 'text'
		const blockUid = deletePrefix(change.blockUid!)
		const content = change.afterContent

		if (blockType === 'defaultTable' && content?.cell) {
			socket.createTableBlock({
				noteUID: noteUid,
				blockUID: blockUid,
				new: false,
				content: {
					type: 'defaultTable',
					tableUID: content.tableUid || '',
					cell: content.cell || [],
				},
			})
		} else {
			const sendContent = blockType === 'chart' ? JSON.stringify(content) : content || ''
			socket.modifyNoteBlock(noteUid, blockUid, sendContent, blockType)
		}
	}

	private syncBlockRestoreSocket(change: Change, noteUid: string, socket: any) {
		const blockType = change.beforeType || change.blockType || 'text'
		const blockUid = deletePrefix(change.blockUid!)
		const content = change.beforeContent

		if (blockType === 'defaultTable' && content?.cell) {
			socket.createTableBlock({
				noteUID: noteUid,
				blockUID: blockUid,
				new: false,
				content: {
					type: 'defaultTable',
					tableUID: content.tableUid || '',
					cell: content.cell || [],
				},
			})
		} else {
			const sendContent = blockType === 'chart' ? JSON.stringify(content) : content || ''
			socket.modifyNoteBlock(noteUid, blockUid, sendContent, blockType)
		}
	}

	private syncCellModifySocket(change: Change, isUndo: boolean, noteUid: string, socket: any) {
		const content = isUndo ? change.beforeContent : change.afterContent
		const dataType = isUndo ? change.beforeDataType : change.afterDataType
		const cellId = addPrefix(change.cellUid!)
		const cellEl = document.getElementById(cellId)
		if (!cellEl) return

		const table = cellEl.closest('table')
		const blockEl = cellEl.closest('.editor--block')
		const tableUid = table ? deletePrefix(table.id) : ''
		const blockUid = blockEl ? deletePrefix(blockEl.id) : ''

		socket.updateCell(noteUid, blockUid, tableUid, change.cellUid, {
			content: content ?? '',
			dataType: dataType || {},
		})
	}

	// ==================== 커서 복원 ====================

	private async restoreCursorAfterDelta(entry: DeltaEntry, isUndo: boolean) {
		const { selector, changeRange, cellTarget } = storeToRefs(useEditorStore())

		const targetSelection = isUndo ? entry.beforeSelection : entry.selection
		const blockUid = targetSelection?.blockUid
		const cellUid = targetSelection?.cellUid
		let blockEl: HTMLElement | null = null

		// 1. 테이블 셀인 경우 cellUid로 먼저 찾기
		if (cellUid) {
			const cellId = addPrefix(cellUid)
			const cellEl = document.getElementById(cellId) as HTMLTableCellElement
			if (cellEl?.tagName === 'TD') {
				cellTarget.value = cellEl
				const tableBlock = cellEl.closest('.editor--block')
				if (tableBlock) blockEl = tableBlock as HTMLElement
			}
		}

		// 2. blockUid로 찾기
		if (!blockEl && blockUid) {
			blockEl = document.getElementById(addPrefix(blockUid))
		}

		// 3. 변경된 블럭 중 첫 번째를 fallback으로
		if (!blockEl || !blockEl.isConnected) {
			const firstChange = entry.changes.find(c => c.blockUid)
			if (firstChange?.blockUid) {
				blockEl = document.getElementById(addPrefix(firstChange.blockUid))
			}
		}

		// 4. 마지막 블럭을 fallback으로
		if (!blockEl || !blockEl.isConnected) {
			const allBlocks = document.querySelectorAll('.editor--block')
			if (allBlocks.length > 0) {
				blockEl = allBlocks[allBlocks.length - 1] as HTMLElement
			}
		}

		if (blockEl && blockEl.isConnected) {
			selector.value = blockEl
			changeRange.value = targetSelection

			await this.restoreCursorWithFallback(blockEl, cellTarget.value, targetSelection)
			await nextTick()
			this.addActiveClassToBlock(blockEl)

			// table--selector 복원
			if (cellTarget.value) {
				await this.restoreTableSelector(cellTarget.value)
			}
		} else {
			// 폴백: 첫 번째 블럭
			const firstBlock = document.querySelector('.editor--block') as HTMLElement
			if (firstBlock?.isConnected) {
				selector.value = firstBlock
				await nextTick()
				await new Promise(requestAnimationFrame)
				this.addActiveClassToBlock(firstBlock)
			}
		}
	}

	private async restoreTableSelector(cellEl: HTMLTableCellElement) {
		const cellDiv = cellEl.querySelector('div[contenteditable]')
		if (!cellDiv) return

		let selectorOverlayEl = document.querySelector('.table--selector__overlay') as HTMLElement | null
		if (!selectorOverlayEl) {
			const parentBlock = cellDiv.closest('.editor--block')
			if (parentBlock) {
				selectorOverlayEl = document.createElement('div')
				selectorOverlayEl.setAttribute('contenteditable', 'false')
				selectorOverlayEl.setAttribute('tabIndex', '-1')
				selectorOverlayEl.classList.add('table--selector__overlay')
				selectorOverlayEl.style.visibility = 'hidden'

				const selectorDiv = document.createElement('div')
				selectorDiv.setAttribute('contenteditable', 'false')
				selectorDiv.setAttribute('tabIndex', '-1')
				selectorDiv.classList.add('table--selector')

				const selectorLeftTopPointer = document.createElement('div')
				selectorLeftTopPointer.setAttribute('contenteditable', 'false')
				selectorLeftTopPointer.setAttribute('tabIndex', '-1')
				selectorLeftTopPointer.classList.add('table--selector__pointer', 'table--selector__pointer-left-top')

				const selectorRightBottomPointer = document.createElement('div')
				selectorRightBottomPointer.setAttribute('contenteditable', 'false')
				selectorRightBottomPointer.setAttribute('tabIndex', '-1')
				selectorRightBottomPointer.classList.add('table--selector__pointer', 'table--selector__pointer-right-bottom')

				selectorOverlayEl.appendChild(selectorDiv)
				selectorOverlayEl.appendChild(selectorLeftTopPointer)
				selectorOverlayEl.appendChild(selectorRightBottomPointer)
				parentBlock.appendChild(selectorOverlayEl)
			}
		}

		await nextTick()
		const selectorOverlay = (await import('@/views/editor/composable/table/selector/selctorOverlay')).useSelectorOverlay()
		selectorOverlay.positionSelector(cellDiv as HTMLDivElement)
	}
}
